import { Routes } from '@angular/router';

/**
 * Public organization routes.
 *
 * Org context comes from:
 * - Production: subdomain (`chamberlain.classjax.com` → org 'chamberlain').
 *   Inner paths mount directly at `/`.
 * - Dev / localhost: path prefix `/o/{orgHandle}/...`. Inner paths mount
 *   under that parent.
 *
 * Inner URL paths (after org context):
 * - /                                              → Org root (single-campus → campus home; multi-campus → org landing)
 * - /:pageHandle                                   → Org-level page (or single-campus's campus page)
 * - /u/:userId                                       → Teacher (at org level)
 * - /u/:userId/class/:classId                        → Class at the teacher's level
 * - /u/:userId/planner/:plannerId                    → Planner at the teacher's level
 * - /campus/:campusHandle                            → Campus home (multi-campus orgs only)
 * - /campus/:campusHandle/:pageHandle                → Campus-level page
 * - /campus/:campusHandle/u/:userId                  → Teacher within a campus
 * - /campus/:campusHandle/u/:userId/class/:classId   → Class within a campus
 * - /campus/:campusHandle/u/:userId/planner/:plannerId → Planner within a campus
 *
 * Org accounts have no per-org handle (account-design Part 4), so the
 * user segment in the org/campus tree is the user_id. Personal pages
 * outside an org context still use the handle — those routes are mounted
 * at the top level in app.routes.ts, not here.
 *
 * Reserved prefixes (cannot be page handles):
 * - /u/ - teacher routes
 * - /campus/ - campus routes
 *
 * Route order is critical — most specific routes must come first.
 */

/**
 * Sharing routes — `/s/{type}/{code}` (e.g. `/s/doc/:documentId`).
 * Universal public share artifacts. Lives in both route trees.
 */
export const shareRoutes: Routes = [
  {
    path: 's/doc/:documentId',
    loadComponent: () =>
      import('./share-document-page/share-document-page.component').then(
        (m) => m.ShareDocumentPageComponent
      ),
  },
  {
    path: 's/planner/:plannerId',
    loadComponent: () =>
      import('./share-planner-page/share-planner-page.component').then(
        (m) => m.SharePlannerPageComponent
      ),
  },
  {
    path: 's/standard/:standardItemId',
    loadComponent: () =>
      import('./share-standard-page/share-standard-page.component').then(
        (m) => m.ShareStandardPageComponent
      ),
  },
  {
    path: 's/bible/:ref',
    loadComponent: () =>
      import('./share-bible-page/share-bible-page.component').then(
        (m) => m.ShareBiblePageComponent
      ),
  },
];

/**
 * Legacy redirect for the old `/s/:siteHandle/...` dev path.
 *
 * Must be registered AFTER `shareRoutes` so specific share artifact paths
 * (`/s/doc/...`, `/s/planner/...`, etc.) win over the generic siteHandle match.
 */
export const legacyOrgPathRoutes: Routes = [
  {
    path: 's/:siteHandle',
    loadComponent: () =>
      import('./legacy-redirect.component').then((m) => m.LegacyRedirectComponent),
  },
  {
    path: 's/:siteHandle/**',
    loadComponent: () =>
      import('./legacy-redirect.component').then((m) => m.LegacyRedirectComponent),
  },
];

export const siteInnerRoutes: Routes = [
  // ===========================================
  // Public org layout - wraps all org pages
  // Uses OrgContextService to resolve org from subdomain, ?org= (dev), or /s/:orgHandle (legacy dev)
  // ===========================================
  {
    path: '',
    loadComponent: () =>
      import('./campus-layout/campus-layout.component').then((m) => m.CampusLayoutComponent),
    children: [
      // === Campus-scoped routes (multi-campus orgs use /campus/:handle) ===
      // Most specific routes first

      // Campus teacher classroom shell: /campus/:campusHandle/u/:userId
      // The shell fetches the teacher and renders the hero + chrome; child
      // routes consume it via TeacherClassroomContext.
      {
        path: 'campus/:campusHandle/u/:userId',
        loadComponent: () =>
          import('./classroom-shell/classroom-shell.component').then(
            (m) => m.ClassroomShellComponent
          ),
        children: [
          {
            path: 'class/:classId',
            loadComponent: () =>
              import('./campus-class-page/campus-class-page.component').then(
                (m) => m.CampusClassPageComponent
              ),
          },
          {
            path: 'planner/:plannerId',
            loadComponent: () =>
              import('./planner-detail-page/planner-detail-page.component').then(
                (m) => m.PlannerDetailPageComponent
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('./classroom-landing/classroom-landing.component').then(
                (m) => m.ClassroomLandingComponent
              ),
          },
        ],
      },

      // Campus page: /campus/:campusHandle/:pageHandle
      {
        path: 'campus/:campusHandle/:pageHandle',
        loadComponent: () =>
          import('./campus-page/campus-page.component').then((m) => m.CampusPageComponent),
      },

      // Campus home: /campus/:campusHandle
      {
        path: 'campus/:campusHandle',
        loadComponent: () =>
          import('./campus-page/campus-page.component').then((m) => m.CampusPageComponent),
      },

      // Redirect bare /campus to root
      { path: 'campus', redirectTo: '', pathMatch: 'full' },

      // === Org-root routes (single-campus orgs OR org-level content) ===
      // Most specific routes first

      // Teacher classroom shell: /u/:userId — same shell as the campus tree,
      // mounted without a campus segment.
      {
        path: 'u/:userId',
        loadComponent: () =>
          import('./classroom-shell/classroom-shell.component').then(
            (m) => m.ClassroomShellComponent
          ),
        children: [
          {
            path: 'class/:classId',
            loadComponent: () =>
              import('./campus-class-page/campus-class-page.component').then(
                (m) => m.CampusClassPageComponent
              ),
          },
          {
            path: 'planner/:plannerId',
            loadComponent: () =>
              import('./planner-detail-page/planner-detail-page.component').then(
                (m) => m.PlannerDetailPageComponent
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('./classroom-landing/classroom-landing.component').then(
                (m) => m.ClassroomLandingComponent
              ),
          },
        ],
      },

      // Redirect bare /u to root
      { path: 'u', redirectTo: '', pathMatch: 'full' },

      // Page: /:pageHandle (3+ chars, catch-all for pages)
      {
        path: ':pageHandle',
        loadComponent: () =>
          import('./campus-page/campus-page.component').then((m) => m.CampusPageComponent),
      },

      // Home: /
      {
        path: '',
        loadComponent: () =>
          import('./campus-page/campus-page.component').then((m) => m.CampusPageComponent),
      },
    ],
  },
];

/**
 * Org public-site routes. On subdomains they mount at `/`. On localhost they
 * mount under `/o/:orgHandle/` (see `pathPrefixOrgSiteRoutes` in app.routes.ts).
 *
 * The `/s/:orgHandle/` dev prefix is gone — use `/o/{handle}/` in dev or just
 * a subdomain in prod.
 */
export const publicSiteRoutes: Routes = siteInnerRoutes;
